import React from 'react';
import { UseFormMethods } from 'react-hook-form';
import {
  DropdownFilterOption,
  RadioGroupOption,
  useDidUpdateEffect,
} from '@odin-labs/components';
import { areArraysEqual, usePrevious } from 'utils';
import { BriefcaseIcon, BuildingColumnsIcon } from 'components/icons';
import { FormInputTypes, GridColSpan, TypedFormInputs } from 'components/form';
import {
  CreateAnnouncementData,
  MakeAnnouncementFormData,
  AnnouncementType,
  JobsiteSelectOptionElement,
} from 'containers/announcements/types';

const announcementTypeOptions: RadioGroupOption<AnnouncementType>[] = [
  {
    label: 'Text Message (SMS)',
    value: AnnouncementType.Sms,
  },
  {
    label: 'Email',
    value: AnnouncementType.Email,
  },
];

export const getFormInputs = (
  jobsiteOptions: JobsiteSelectOptionElement[],
  developerOptions: DropdownFilterOption[],
): TypedFormInputs<MakeAnnouncementFormData> => ({
  ...(developerOptions?.length && {
    developerIds: {
      element: FormInputTypes.Select,
      label: 'Client',
      elementProps: {
        placeholder: 'Select client',
        options: developerOptions,
        icon: BuildingColumnsIcon,
        disabled: (developerOptions?.length ?? 0) < 1,
        isMulti: true,
        clearToNull: true,
      },
      validation: {},
      layout: GridColSpan.SpanFull,
    },
  }),
  jobsiteIds: {
    element: FormInputTypes.Select,
    label: 'Jobsite',
    elementProps: {
      placeholder: 'Select jobsite',
      options: jobsiteOptions,
      icon: BriefcaseIcon,
      disabled: (jobsiteOptions?.length ?? 0) < 1,
      isMulti: true,
      clearToNull: true,
    },
    validation: { required: true },
    layout: GridColSpan.SpanFull,
  },
  announcementType: {
    element: FormInputTypes.RadioGroup,
    label: 'Type',
    elementProps: {
      options: announcementTypeOptions,
      preventSubmitOnEnter: true,
      layout: 'horizontal',
    },
    validation: { required: true },
    layout: GridColSpan.SpanFull,
  },
});

type MakeAnnouncementFormDataInputHook = {
  jobsiteOptions: JobsiteSelectOptionElement[];
  developerOptions: DropdownFilterOption[];
  announcement: CreateAnnouncementData;
};

type ChangeSource = 'developerSelection' | 'jobsiteSelection';

// Manages form by keeping developer and jobsite selections in sync
export const getFormInputsHook =
  (args: MakeAnnouncementFormDataInputHook) =>
  ({
    watch,
    setValue,
  }: UseFormMethods<MakeAnnouncementFormData>): TypedFormInputs<MakeAnnouncementFormData> => {
    const { jobsiteOptions, developerOptions } = args;
    const { developerIds, jobsiteIds } = watch(['developerIds', 'jobsiteIds']);
    const prevDeveloperIds = usePrevious(developerIds);
    const canSendDeveloperWide = !!developerOptions?.length;

    const changeSource = React.useRef<ChangeSource | null>(null);
    const wasChangeTriggeredFrom = (source: ChangeSource): boolean => {
      if (changeSource.current === source) {
        changeSource.current = null;
        return true;
      }
      return false;
    };
    const willTriggerChangeFrom = (source: ChangeSource): void => {
      changeSource.current = source;
    };

    // This effect ensures that when developers are selected,
    // corresponding jobsites are also selected and previously selected jobsites are preserved.
    // When developers are removed, jobsites associated with them are also removed.
    // If the change was triggered from the jobsite selection, it will not run.
    useDidUpdateEffect(() => {
      if (!canSendDeveloperWide) return;
      if (wasChangeTriggeredFrom('jobsiteSelection')) return;

      // Filter jobsite options by selected developers and active jobsites
      const today = new Date();
      const selectedDevelopersJobsiteIds =
        jobsiteOptions?.filter(({ jobsite }) => {
          const belongsToSelectedDeveloper = developerIds?.some(
            (d) => d.value === jobsite.developer.developerId,
          );
          if (!belongsToSelectedDeveloper) return false;

          const { endDate } = jobsite;
          return !endDate || new Date(endDate) >= today;
        }) ?? [];

      // Developer IDs that were removed from the selection
      const removedDeveloperIds =
        prevDeveloperIds?.filter(
          (prev) => !developerIds?.some((d) => d.value === prev.value),
        ) ?? [];

      // Keep existing jobsites, except those that belong to removed developers
      const developerIdsToExclude =
        removedDeveloperIds.length > 0 ? removedDeveloperIds : [];
      const selectedJobsiteIdsNotAssignedToSelectedDevelopers =
        jobsiteIds?.filter(({ jobsite }) => {
          return !developerIdsToExclude.some(
            (d) => d.value === jobsite.developer.developerId,
          );
        }) ?? [];

      // Strip duplicates from the new jobsite IDs and merge jobsites lists
      const newJobsiteIds = Array.from(
        new Map(
          [
            ...selectedJobsiteIdsNotAssignedToSelectedDevelopers,
            ...selectedDevelopersJobsiteIds,
          ].map((opt) => [opt.value, opt]),
        ).values(),
      );

      const selectedJobsitesShouldChange = !areArraysEqual(
        jobsiteIds?.map((js) => js.value),
        newJobsiteIds.map((js) => js.value),
      );

      if (selectedJobsitesShouldChange) {
        willTriggerChangeFrom('developerSelection');
        const newJobsiteIdsValue = newJobsiteIds?.length ? newJobsiteIds : null;
        setValue('jobsiteIds', newJobsiteIdsValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }, [developerIds]);

    // This effect ensures that when all jobsites corresponding to a
    // selected developer are removed, the developer is also removed from the selection.
    // If the change was triggered from the developer selection, it will not run.
    useDidUpdateEffect(() => {
      if (!canSendDeveloperWide) return;
      if (wasChangeTriggeredFrom('developerSelection')) return;

      const newDeveloperIds =
        // Filter out developers that do not have any jobsites selected.
        developerIds?.filter((d) =>
          jobsiteIds?.some(
            ({ jobsite }) => jobsite.developer.developerId === d.value,
          ),
        ) ?? [];

      if (
        developerIds?.length &&
        newDeveloperIds.length < developerIds.length
      ) {
        willTriggerChangeFrom('jobsiteSelection');
        const newDeveloperIdsValue = newDeveloperIds?.length
          ? newDeveloperIds
          : null;
        setValue('developerIds', newDeveloperIdsValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }, [jobsiteIds]);

    return React.useMemo(
      () => getFormInputs(jobsiteOptions, developerOptions),
      [jobsiteOptions, developerOptions],
    );
  };

export const getDefaultValues = (
  jobsiteOptions: JobsiteSelectOptionElement[],
  developerOptions: DropdownFilterOption[],
  announcement: CreateAnnouncementData,
): MakeAnnouncementFormData => {
  const developerIds = developerOptions?.filter((d) =>
    announcement.filterDeveloperIds?.includes(d.value),
  );
  const jobsiteIds =
    jobsiteOptions?.length === 1
      ? [jobsiteOptions[0]]
      : jobsiteOptions?.filter((jo) =>
          announcement.filterJobsiteIds?.includes(jo.value),
        );
  return {
    developerIds: developerIds?.length ? developerIds : null,
    jobsiteIds: jobsiteIds?.length ? jobsiteIds : null,
    announcementType: announcement.announcementType
      ? announcementTypeOptions.find(
          (ao) => ao.value === announcement.announcementType,
        ) ?? null
      : announcementTypeOptions[0],
  };
};
